import React, { useEffect } from 'react';
import { Box, Divider, styled, SxProps, Tab, Tabs, Theme } from '@mui/material';
import { TabsProps } from '@mui/material/Tabs';
import { useIntl } from 'react-intl';
import { useCurrentPatientGuid } from '../hooks/route/useCurrentPatientGuid';
import { PatientList } from '../channel/PatientList';
import { ChannelList } from '../channel/ChannelList';
import { CommunityChannelList } from '../../community/CommunityChannelList';
import { useCommunityOptInStatus } from '../hooks/store/useCommunitySla';
import { useNavigate } from 'react-router';
import { Routes } from '@xolv-mybrightlink/common';
import { CommunityTabTitle } from '../../community/CommunityTabTitle';

export const CareTeamTab = 0;
export const CommunityTab = 1;
export type MessageTab = 0 | 1;

const TabPanel = (props: {
  children?: React.ReactNode;
  tab: MessageTab;
  value: MessageTab;
  sx?: SxProps<Theme>;
}) => {
  const { children, value, tab, sx, ...other } = props;

  return (
    <Box
      role='tabpanel'
      hidden={value !== tab}
      id={`simple-tabpanel-${tab}`}
      aria-labelledby={`simple-tab-${tab}`}
      {...other}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        ...sx,
      }}
    >
      {value === tab && children}
    </Box>
  );
};

const a11yProps = (tab: MessageTab) => {
  return {
    id: `simple-tab-${tab}`,
    'aria-controls': `simple-tabpanel-${tab}`,
  };
};

const StyledTabs = styled(Tabs)<TabsProps>(() => ({
  margin: '0 20px 0 20px',
  fontWeight: 'normal',
  fontSize: 14,
  '& .Mui-selected': {
    fontWeight: 600,
  },
}));

type MessageBarTabViewProps = {
  defaultTab: MessageTab;
  onChangeTab: (event: React.SyntheticEvent, newTab: MessageTab) => void;
  defaultPatientGuid?: string;
  onSelectChannel: (channelGuid: string) => void;
  onSelectPatient: (patientGuid: string) => void;
  standalone?: boolean;
};

export const MessageBarTabView = (props: MessageBarTabViewProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const tab = props.defaultTab;
  const currentPatientGuid = useCurrentPatientGuid();
  const communityOptedIn = useCommunityOptInStatus();
  const patientGuid = props.defaultPatientGuid ?? currentPatientGuid;
  const standalone = props.standalone ?? false;

  useEffect(() => {
    if (tab === CommunityTab && !communityOptedIn && !standalone) {
      navigate(Routes.messages.community.sla, { replace: true });
    }
  }, [tab, navigate, communityOptedIn, standalone]);

  return (
    <>
      <StyledTabs
        value={tab}
        onChange={props.onChangeTab}
        aria-label='basic tabs example'
        variant='fullWidth'
      >
        <Tab
          label={intl.formatMessage({ id: 'messages.care-team' })}
          sx={{ whiteSpace: 'nowrap' }}
          {...a11yProps(CareTeamTab)}
        />
        <Tab
          label={<CommunityTabTitle />}
          sx={{ whiteSpace: 'nowrap' }}
          {...a11yProps(CommunityTab)}
        />
      </StyledTabs>
      <Divider sx={{ height: 30, borderBottomWidth: 0 }} />
      <TabPanel tab={CareTeamTab} value={tab}>
        <PatientList
          defaultPatientGuid={patientGuid}
          onSelectPatient={props.onSelectPatient}
        />
        <Divider sx={{ height: 30, borderBottomWidth: 0 }} />
        <ChannelList
          patientGuid={patientGuid}
          onSelect={props.onSelectChannel}
        />
      </TabPanel>
      <TabPanel tab={CommunityTab} value={tab} sx={{ flex: 2 }}>
        <CommunityChannelList
          onSelect={props.onSelectChannel}
          standalone={standalone}
        />
      </TabPanel>
    </>
  );
};
